import { useState, useMemo } from 'react'
import { useMetaData } from '../queries/useMetaData.js'
import { useGoalsData } from '../queries/useGoalsData.js'
import { useActionItemOp } from '../mutations/useActionItemOp.js'
import { useUIStore } from '../store/uiStore.js'
import { colorFor } from '../data/userColors.js'
import { PILLARS } from '../data/pillars.js'

/**
 * FollowUpsSection — deterministic list of all open action items across
 * every goal and pillar, sitting inside the Executive Summary Card.
 *
 * Why deterministic (not AI-generated):
 *   Open action items are structured data. Asking Claude to list them
 *   would risk hallucination or omission, and the user needs to ACT on
 *   each item (mark done, reassign). A live interactive list handles
 *   that far better than a static AI paragraph. The AI summary stays
 *   focused on narrative; this section handles operations.
 *
 * Shape:
 *   Collapsible header with counts: "🎯 Follow-ups (N open, M overdue)"
 *   Grouped by pillar › goal (same convention as the archive panel)
 *   Per item: checkbox → toggles done, owner chip, overdue/due badge
 *   Sort order: overdue first, then by dueDate ascending, then by
 *               createdAt (oldest unfinished business first)
 *   Pillar click jumps to that pillar's tab for convenience.
 *
 * Empty state shows only if NOTHING is open — the common "lots of stuff"
 * case renders without a "there's nothing here" fallback.
 */
export default function FollowUpsSection({ defaultExpanded = false }) {
  const { data: meta } = useMetaData()
  const { data: goalsData } = useGoalsData()
  const opAction = useActionItemOp()
  const setActivePillar = useUIStore((s) => s.setActivePillar)
  const [expanded, setExpanded] = useState(defaultExpanded)

  // Build groups of open action items keyed by goal, in pillar order.
  const groups = useMemo(() => {
    if (!Array.isArray(goalsData)) return []
    const actionItems = meta?.actionItems || {}
    const out = []
    for (const [key, items] of Object.entries(actionItems)) {
      if (!Array.isArray(items)) continue
      const open = items.filter((a) => a.status !== 'done')
      if (open.length === 0) continue
      const match = /^p(\d+)-g(\d+)$/.exec(key)
      if (!match) continue
      const pi = Number(match[1])
      const gi = Number(match[2])
      const pillar = goalsData[pi]
      const goal = pillar?.goals?.[gi]
      if (!goal) continue
      out.push({
        key,
        pi,
        gi,
        pillar: pillar.pillar,
        goalName: goal.goal || 'Untitled goal',
        items: sortItems(open),
      })
    }
    // Stable pillar/goal order
    return out.sort((a, b) => (a.pi - b.pi) || (a.gi - b.gi))
  }, [meta?.actionItems, goalsData])

  const totals = useMemo(() => {
    let open = 0
    let overdue = 0
    for (const g of groups) {
      for (const item of g.items) {
        open += 1
        if (isOverdue(item)) overdue += 1
      }
    }
    return { open, overdue }
  }, [groups])

  if (totals.open === 0) {
    return (
      <div
        style={{
          fontSize: 11,
          color: 'var(--muted-light)',
          fontStyle: 'italic',
          padding: '8px 0',
        }}
      >
        🎯 No open action items — nothing to follow up on.
      </div>
    )
  }

  const jumpToPillar = (pillarName) => {
    const idx = PILLARS.findIndex((p) => p.name === pillarName)
    if (idx >= 0) setActivePillar(idx)
  }

  const handleToggle = (group, item) => {
    opAction.mutate({
      op: 'toggle',
      goalKey: group.key,
      itemId: item.id,
      optimistic: (list) =>
        list.map((a) =>
          a.id === item.id
            ? {
                ...a,
                status: a.status === 'done' ? 'open' : 'done',
                completedAt:
                  a.status === 'done' ? null : new Date().toISOString(),
              }
            : a
        ),
    })
  }

  return (
    <div>
      {/* Header — click to expand */}
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        style={{
          background: 'none',
          border: 'none',
          fontSize: 11,
          fontWeight: 700,
          color: 'var(--muted)',
          cursor: 'pointer',
          padding: 0,
          textTransform: 'uppercase',
          letterSpacing: 0.06,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        {expanded ? '▾' : '▸'} 🎯 Follow-ups ({totals.open} open
        {totals.overdue > 0 && (
          <span style={{ color: '#b91c1c' }}>, {totals.overdue} overdue</span>
        )}
        )
      </button>

      {expanded && (
        <div
          style={{
            marginTop: 10,
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
          }}
        >
          {groups.map((g) => (
            <div key={g.key}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 6,
                  marginBottom: 6,
                }}
              >
                <button
                  type="button"
                  onClick={() => jumpToPillar(g.pillar)}
                  title={`Jump to ${g.pillar}`}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    fontSize: 10,
                    fontWeight: 700,
                    color: 'var(--muted)',
                    textTransform: 'uppercase',
                    letterSpacing: 0.06,
                    cursor: 'pointer',
                  }}
                >
                  {g.pillar}
                </button>
                <span style={{ color: 'var(--muted-light)', fontSize: 11 }}>›</span>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: 'var(--text-secondary)',
                  }}
                >
                  {g.goalName}
                </span>
              </div>
              <ul
                style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                }}
              >
                {g.items.map((item) => (
                  <FollowUpRow
                    key={item.id}
                    item={item}
                    onToggle={() => handleToggle(g, item)}
                  />
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/* ─── Row ─────────────────────────────────────────────────────────── */

function FollowUpRow({ item, onToggle }) {
  const overdue = isOverdue(item)
  const owner = item.owner && item.owner !== '—' ? item.owner : null
  return (
    <li
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 8px',
        background: overdue ? '#fef2f2' : '#fafafa',
        border: `1px solid ${overdue ? '#fecaca' : 'var(--border)'}`,
        borderRadius: 6,
        fontSize: 12,
      }}
    >
      <input
        type="checkbox"
        checked={false}
        onChange={onToggle}
        title="Mark as done"
        style={{ flexShrink: 0, cursor: 'pointer' }}
      />
      <span style={{ flex: 1, color: 'var(--text)', lineHeight: 1.4 }}>
        {item.task}
      </span>
      {owner && (
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            background: colorFor(owner),
            color: '#fff',
            padding: '2px 6px',
            borderRadius: 4,
            whiteSpace: 'nowrap',
          }}
        >
          {owner}
        </span>
      )}
      {item.dueDate && (
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: overdue ? '#b91c1c' : 'var(--muted)',
            whiteSpace: 'nowrap',
          }}
          title={overdue ? `Overdue — was due ${item.dueDate}` : `Due ${item.dueDate}`}
        >
          {overdue ? `⚠ ${item.dueDate}` : item.dueDate}
        </span>
      )}
    </li>
  )
}

/* ─── Helpers ─────────────────────────────────────────────────────── */

function isOverdue(item) {
  if (!item.dueDate) return false
  const today = new Date().toISOString().slice(0, 10)
  return item.dueDate < today
}

function sortItems(items) {
  return [...items].sort((a, b) => {
    const ao = isOverdue(a)
    const bo = isOverdue(b)
    if (ao !== bo) return ao ? -1 : 1
    // Both overdue or both not: sort by due date ascending. Items with a
    // due date come before items without one, so dated work surfaces first.
    if (a.dueDate && b.dueDate) return a.dueDate.localeCompare(b.dueDate)
    if (a.dueDate) return -1
    if (b.dueDate) return 1
    // Neither has a due date — oldest unfinished business first.
    const aCreated = a.createdAt || ''
    const bCreated = b.createdAt || ''
    return aCreated.localeCompare(bCreated)
  })
}
